/**
 * Rank system data
 */

export interface RankDef {
  grade: string;
  nameKey: string;
  condition: RankCondition;
  promotionCost: number; // IP cost to promote
  tapMultiplier: number; // permanent tap multiplier at this rank
}

export type RankCondition =
  | { type: 'start' }
  | { type: 'ip'; amount: number }
  | { type: 'prestige'; count: number };

export const RANKS: RankDef[] = [
  { grade: 'E', nameKey: 'rank_e', condition: { type: 'start' }, promotionCost: 0, tapMultiplier: 1.0 },
  { grade: 'D', nameKey: 'rank_d', condition: { type: 'ip', amount: 10000 }, promotionCost: 5_000, tapMultiplier: 1.3 },
  { grade: 'C', nameKey: 'rank_c', condition: { type: 'ip', amount: 1000000 }, promotionCost: 500_000, tapMultiplier: 1.6 },
  { grade: 'B', nameKey: 'rank_b', condition: { type: 'ip', amount: 1000000000 }, promotionCost: 50_000_000, tapMultiplier: 2.0 },
  { grade: 'A', nameKey: 'rank_a', condition: { type: 'ip', amount: 1000000000000 }, promotionCost: 50_000_000_000, tapMultiplier: 2.5 },
  { grade: 'S', nameKey: 'rank_s', condition: { type: 'prestige', count: 1 }, promotionCost: 500_000_000_000, tapMultiplier: 3.0 },
  { grade: 'SS', nameKey: 'rank_ss', condition: { type: 'prestige', count: 5 }, promotionCost: 5_000_000_000_000, tapMultiplier: 4.0 },
  { grade: 'SSS', nameKey: 'rank_sss', condition: { type: 'prestige', count: 15 }, promotionCost: 50_000_000_000_000, tapMultiplier: 5.0 },
];

/** Get the highest rank the player qualifies for (condition-wise) */
export function getEligibleRank(totalIpEarned: number, prestigeCount: number): RankDef {
  let current = RANKS[0];
  for (const rank of RANKS) {
    const cond = rank.condition;
    if (cond.type === 'start') {
      current = rank;
    } else if (cond.type === 'ip' && totalIpEarned >= cond.amount) {
      current = rank;
    } else if (cond.type === 'prestige' && prestigeCount >= cond.count) {
      current = rank;
    }
  }
  return current;
}

/** Get current promoted rank from state */
export function getCurrentRank(rankGrade: string): RankDef {
  return RANKS.find(r => r.grade === rankGrade) ?? RANKS[0];
}

/** Get the next rank available for promotion */
export function getNextPromotableRank(rankGrade: string, totalIpEarned: number, prestigeCount: number): RankDef | null {
  const current = getCurrentRank(rankGrade);
  const currentIdx = RANKS.indexOf(current);
  const next = RANKS[currentIdx + 1];
  if (!next) return null;

  // Check if player meets condition for next rank
  const cond = next.condition;
  if (cond.type === 'ip' && totalIpEarned < cond.amount) return null;
  if (cond.type === 'prestige' && prestigeCount < cond.count) return null;

  return next;
}
